import React from 'react';

const colorClasses = {
  blue: {
    ring: 'bg-gradient-to-br from-sky-500 to-sky-600',
    soft: 'bg-sky-50 text-sky-600',
    value: 'text-sky-700',
  },
  purple: {
    ring: 'bg-gradient-to-br from-secondary-500 to-secondary-600',
    soft: 'bg-secondary-50 text-secondary-600',
    value: 'text-secondary-700',
  },
  green: {
    ring: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    soft: 'bg-emerald-50 text-emerald-600',
    value: 'text-emerald-700',
  },
  orange: {
    ring: 'bg-gradient-to-br from-amber-500 to-amber-600',
    soft: 'bg-amber-50 text-amber-600',
    value: 'text-amber-700',
  },
  red: {
    ring: 'bg-gradient-to-br from-rose-500 to-rose-600',
    soft: 'bg-rose-50 text-rose-600',
    value: 'text-rose-700',
  },
  indigo: {
    ring: 'bg-gradient-to-br from-primary-600 to-primary-700',
    soft: 'bg-primary-50 text-primary-700',
    value: 'text-primary-700',
  },
  teal: {
    ring: 'bg-gradient-to-br from-primary-500 to-teal-600',
    soft: 'bg-primary-50 text-primary-700',
    value: 'text-primary-700',
  },
  pink: {
    ring: 'bg-gradient-to-br from-pink-500 to-rose-500',
    soft: 'bg-pink-50 text-pink-600',
    value: 'text-pink-700',
  },
  slate: {
    ring: 'bg-gradient-to-br from-slate-600 to-slate-700',
    soft: 'bg-slate-100 text-slate-600',
    value: 'text-slate-900',
  },
};

/**
 * StatCard — dashboard tile with title, large value, optional icon + trend.
 * Backward compatible: accepts `title`, `value`, `icon`, `trend`, `color`.
 * Added: `subtitle`, `loading`, `onClick`.
 */
export const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'indigo',
  loading = false,
  onClick,
  restricted = false,
  restrictReason,
  className = '',
}) => {
  const palette = colorClasses[color] || colorClasses.indigo;
  const clickable = typeof onClick === 'function' && !restricted;

  const wrapperClass = [
    'relative group bg-white/95 backdrop-blur-sm rounded-xl border border-slate-200/70',
    'shadow-soft p-3.5 sm:p-4 overflow-hidden',
    clickable
      ? 'cursor-pointer hover:shadow-medium hover:-translate-y-0.5 transition-all duration-300 ease-swift text-left w-full'
      : restricted
        ? 'cursor-default text-left w-full ring-1 ring-slate-100'
        : 'transition-shadow duration-300 ease-swift hover:shadow-medium',
    className,
  ].join(' ');

  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? { type: 'button', onClick }
    : restricted && restrictReason
      ? { title: restrictReason }
      : {};

  return (
    <Wrapper className={wrapperClass} {...wrapperProps}>
      {/* Subtle accent stripe */}
      <span className={`absolute top-0 left-0 right-0 h-1 ${palette.ring} opacity-70`} aria-hidden="true" />

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p
            className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.06em] leading-tight break-words"
            title={typeof title === 'string' ? title : undefined}
          >
            {title}
          </p>
          {restricted ? (
            <p className="mt-0.5 text-[10px] font-medium text-slate-400 normal-case tracking-normal">
              View only
            </p>
          ) : null}
          <p className={`mt-1 text-xl sm:text-2xl font-bold tracking-tight tabular-nums ${palette.value}`}>
            {loading ? <span className="skeleton inline-block h-6 w-14 align-middle" /> : value}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
          )}
          {trend && (
            <div className="mt-2 flex items-center gap-1">
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-md ${
                  trend.positive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}
              >
                <span aria-hidden="true">{trend.positive ? '↑' : '↓'}</span>
                {trend.value}
              </span>
              <span className="text-xs text-slate-500">{trend.label}</span>
            </div>
          )}
        </div>
        {icon && (
          <div
            className={`shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl ${palette.ring} text-white flex items-center justify-center shadow-md [&_svg]:h-4 [&_svg]:w-4 sm:[&_svg]:h-[1.15rem] sm:[&_svg]:w-[1.15rem]`}
          >
            {icon}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default StatCard;
